/* Ameda - For Hospitals: catalogue category filter
   ---------------------------------------------------------------------------
   Loaded only on the For Hospitals template, from snippets/meta-tags.liquid.

   WHY THIS IS NOT IN sections/hospital-catalogue.liquid.

   The catalogue already renders every category as a .amh-cat section carrying
   its own label and its own product count, and hospital-nav.liquid already
   reads those counts from the page rather than recomputing them, for a stated
   reason: the catalogue excludes warranty-only stock from both its listing and
   its totals, so a second count anywhere else would be a second source of
   truth and would drift the first time the exclusion list is edited. This file
   follows that existing rule. Every label, every count and every product comes
   from what the catalogue rendered. Nothing about the range is written here.

   WHAT THIS DELIBERATELY DOES NOT TOUCH.

   The enquiry list lives in localStorage under ameda:enquiry and is shared with
   the hospital product pages. Filtering only toggles a class on a category
   section: no card is created, destroyed, re-rendered or moved, so an item
   added under one filter is still added under every other one and after
   clearing the filter. The enquiry engine, the tray, the quantity steppers,
   the specifications drawer and the copy-codes action are untouched by this
   file. If that ever stops being true, this is the wrong approach.

   Without JavaScript the catalogue renders exactly as it does today: every
   category, every product, nothing hidden. The filter is an enhancement over a
   page that already works.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  function init() {
    var root = document.querySelector('.amh');
    if (!root) return;
    var inner = root.querySelector('.amh__inner');
    if (!inner || inner.querySelector('[data-amhf]')) return;

    var cats = Array.prototype.slice.call(root.querySelectorAll('.amh-cat'));
    if (cats.length < 2) return;

    // Read the catalogue, do not describe it.
    var groups = cats.map(function (cat) {
      var label = cat.querySelector('.amh-cat__label');
      var count = cat.querySelector('.amh-cat__count');
      return {
        el: cat,
        id: cat.id,
        label: label ? label.textContent.trim() : '',
        n: cat.querySelectorAll('[data-amh-card]').length,
        badge: count ? count.textContent.trim() : ''
      };
    }).filter(function (g) { return g.id && g.label; });

    if (groups.length < 2) return;

    var total = groups.reduce(function (sum, g) { return sum + g.n; }, 0);

    var bar = document.createElement('div');
    bar.className = 'amhf';
    bar.setAttribute('data-amhf', '');

    var row = document.createElement('div');
    row.className = 'amhf__row';
    row.setAttribute('role', 'group');
    row.setAttribute('aria-label', 'Filter the hospital range by category');

    function chip(key, label, n) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'amhf__chip';
      b.setAttribute('data-amhf-cat', key);
      b.setAttribute('aria-pressed', key === 'all' ? 'true' : 'false');
      b.appendChild(document.createTextNode(label));
      var s = document.createElement('span');
      s.className = 'amhf__n';
      s.setAttribute('aria-hidden', 'true');
      s.textContent = String(n);
      b.appendChild(s);
      return b;
    }

    row.appendChild(chip('all', 'View all', total));
    groups.forEach(function (g) { row.appendChild(chip(g.id, g.label, g.n)); });

    // role=status so the change is announced without stealing focus. The count
    // is the number of cards actually on screen, counted after filtering.
    var res = document.createElement('p');
    res.className = 'amhf__res';
    res.setAttribute('role', 'status');
    res.setAttribute('aria-live', 'polite');

    bar.appendChild(row);
    bar.appendChild(res);
    inner.insertBefore(bar, groups[0].el);

    function apply(key) {
      var shown = 0;
      groups.forEach(function (g) {
        var on = key === 'all' || g.id === key;
        g.el.classList.toggle('amh-cat--off', !on);
        if (on) shown += g.n;
      });
      row.querySelectorAll('[data-amhf-cat]').forEach(function (b) {
        b.setAttribute('aria-pressed', String(b.getAttribute('data-amhf-cat') === key));
      });
      res.textContent = shown === 1 ? '1 product' : shown + ' products';

      // Nothing may come to REST behind the toolbar. scroll-margin keeps any
      // anchor jump clear of it, and clearFirst() below handles the filter
      // case, where the page does not scroll at all but the category that
      // becomes first can land underneath.
      clearance();
    }

    // Measured, not assumed: the toolbar's height changes with the viewport
    // and with whether the count sits beside the chips or under them.
    function clearance() {
      var h = Math.round(bar.getBoundingClientRect().height);
      root.style.setProperty('--amhf-clear', (chrome() + h + 16) + 'px');
    }

    // After a filter the page has not scrolled, so the category that is now
    // first can be sitting behind the pinned toolbar - or, if the reader was
    // deep in a long category, above the viewport entirely once the page
    // shortens. Either way they are looking at the wrong thing. Bring the
    // first surviving heading to just under the toolbar.
    function clearFirst() {
      var first = groups.filter(function (g) { return !g.el.classList.contains('amh-cat--off'); })[0];
      if (!first) return;

      // NO "is the reader in the catalogue" GUARD. There was one, testing
      // whether the toolbar was still in the viewport, and it was measuring
      // the wrong moment: hiding four categories can shorten the page by
      // 2,500px, so by the time this runs the toolbar the reader just pressed
      // is above the fold and the guard refused to act - leaving them 3,400px
      // past the results they asked for. This is only ever called from a chip
      // press, and a chip can only be pressed while it is on screen, so the
      // question is already answered.
      var barBox = bar.getBoundingClientRect();

      var head = first.el.querySelector('.amh-cat__head') || first.el;
      var clear = chrome() + Math.round(barBox.height) + 16;
      var now = head.getBoundingClientRect().top;

      // Already sitting comfortably below the toolbar: leave the reader alone.
      if (now >= clear && now < window.innerHeight * 0.6) return;

      // LET THE BROWSER DO THE ARITHMETIC. Two hand-rolled versions of this
      // got it wrong in different ways - one measured a sticky bar that moves
      // as you scroll, the other measured before the browser had finished
      // clamping the scroll to a page that had just got 2,500px shorter.
      // scrollIntoView reads scroll-margin-top, which is already set from the
      // measured toolbar height, and it does it after layout rather than
      // guessing at it.
      first.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    row.addEventListener('click', function (e) {
      var b = e.target.closest('[data-amhf-cat]');
      if (!b) return;
      apply(b.getAttribute('data-amhf-cat'));

      // Two frames, not a direct call. Hiding categories shortens the page,
      // and the browser clamps the scroll position afterwards rather than
      // during: called synchronously, clearFirst measured geometry that was
      // about to move and left the reader 700px above the results. Waiting for
      // the clamp to land means it measures what the reader will actually see.
      requestAnimationFrame(function () { requestAnimationFrame(clearFirst); });
      // Keep the pressed chip in view when the row is scrolled sideways on a
      // phone, without scrolling the page itself.
      if (b.scrollIntoView) { b.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }
    });

    // Anything that links to a category anchor has to clear the filter first,
    // or it would scroll to a section this control is holding hidden.
    document.addEventListener('click', function (e) {
      var a = e.target.closest('a[href*="#cat-"]');
      if (!a) return;
      var id = a.getAttribute('href').split('#').pop();
      var hit = groups.some(function (g) { return g.id === id; });
      if (hit) { apply('all'); }
    });

    // The rail's own count logic looks for a .amh-cat__count inside whatever a
    // link points at. Now that the rail points at the catalogue as a whole
    // rather than at one category, that finds the FIRST category's badge and
    // labels the whole range with it - "Hospital range 2" for 18 products.
    // Corrected here rather than in hospital-nav.liquid, which is a generic
    // section: the catalogue link gets the real total and every other rail
    // link, none of which is a category any more, loses its superscript.
    function railCounts() {
      var links = document.querySelectorAll('.amn [data-amn-link]');
      Array.prototype.forEach.call(links, function (l) {
        var sup = l.querySelector('[data-amn-count]');
        if (!sup) return;
        var id = l.getAttribute('data-target');
        if (id === root.id || id === 'hospital-catalogue') {
          sup.textContent = String(total);
          sup.hidden = false;
        } else if (!groups.some(function (g) { return g.id === id; })) {
          sup.textContent = '';
          sup.hidden = true;
        }
      });
    }

    // Called again after load. The rail fills its counts from a
    // DOMContentLoaded handler it registers while the page is still parsing,
    // and the exact interleaving with a deferred script is not worth betting a
    // wrong product count on, so this simply runs last as well.
    railCounts();
    window.addEventListener('load', railCounts);

    // WHAT IS ACTUALLY PINNED ABOVE IS NOT WHAT IT LOOKS LIKE, so the offset
    // is measured rather than assumed. The nav rail carries position:sticky
    // and a "Pin to top on scroll" setting, but its Shopify section wrapper is
    // exactly the rail's own height, which leaves sticky no travel: measured
    // mid-catalogue the rail sits 1,125px above the viewport. It has never
    // pinned. Dawn's header is what pins, revealing on scroll up, so its
    // height is the offset. The rail is still measured in, in case that
    // wrapper ever gains room and the setting starts doing what it says.
    function chrome() {
      var h = 0;
      var hdr = document.querySelector('.section-header');
      if (hdr && getComputedStyle(hdr).position === 'sticky') {
        h += Math.round(hdr.getBoundingClientRect().height);
      }
      var rail = document.querySelector('.amn--stick');
      if (rail && rail.parentElement) {
        var room = rail.parentElement.getBoundingClientRect().height - rail.getBoundingClientRect().height;
        if (room > 1) { h += Math.round(rail.getBoundingClientRect().height); }
      }
      return h;
    }
    function offset() {
      var h = chrome();
      bar.style.setProperty('--amhf-top', h ? h + 'px' : '0px');
    }
    offset();
    clearance();
    window.addEventListener('resize', clearance);
    if (window.ResizeObserver) {
      var hdrEl = document.querySelector('.section-header');
      if (hdrEl) { new ResizeObserver(offset).observe(hdrEl); }
    }
    window.addEventListener('resize', offset);

    // Fresh load shows the whole range, always. Nothing is remembered.
    apply('all');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
